import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { invoke } from "@tauri-apps/api/core";
import { Download, RefreshCw, X } from "lucide-react";
import { Button } from "@link/ui";
import { updateIsBlocked, withUpdateInstall } from "../lib/update-activity";
import { desktopRuntime, desktopAppLabel } from "../lib/client-version";
import { ClientVersionDetails } from "../components/ClientVersionDetails";

type Phase = "disabled" | "idle" | "checking" | "current" | "available" | "downloading" | "ready" | "installing" | "error";
export interface UpdateStatus {
  phase: Phase;
  currentVersion: string;
  version: string | null;
  downloaded: number;
  total: number | null;
  message: string | null;
}
type Operation = "status" | "check" | "download" | "install";
const marker = desktopRuntime;
const initial = (): UpdateStatus => ({ phase: marker()?.updater ? "idle" : "disabled", currentVersion: marker()?.version ?? "", version: null, downloaded: 0, total: null, message: null });
interface UpdatesValue { status: UpdateStatus; busy: boolean; callBusy: boolean; run: (operation: Operation) => Promise<void> }
const Context = createContext<UpdatesValue | null>(null);
const SIX_HOURS = 6 * 60 * 60 * 1000;

export function DesktopUpdatesProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [callBusy, setCallBusy] = useState(false);
  const [dismissed, setDismissed] = useState<string | null>(null);
  const inFlight = useRef(false);
  const alive = useRef(true);
  const run = useCallback(async (operation: Operation) => {
    if (!marker()?.updater || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const request = () => invoke<UpdateStatus>("desktop_update", { operation });
      const next = operation === "install" ? await withUpdateInstall(request) : await request();
      if (alive.current) setStatus(next);
    } catch (error) {
      if (alive.current) setStatus(current => ({ ...current, message: operation === "install" && error instanceof Error ? error.message : "Couldn't reach Link's updater. You can keep using Link and try again later." }));
    } finally {
      inFlight.current = false;
      if (alive.current) setBusy(false);
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    if (!marker()?.updater) return () => { alive.current = false; };
    void run("status");
    const startup = window.setTimeout(() => void run("check"), 15_000);
    const interval = window.setInterval(() => void run("check"), SIX_HOURS);
    const online = () => void run("check");
    window.addEventListener("online", online);
    return () => {
      alive.current = false;
      window.clearTimeout(startup);
      window.clearInterval(interval);
      window.removeEventListener("online", online);
    };
  }, [run]);

  useEffect(() => {
    if (!marker()?.updater) return;
    // Poll only local state: progress never sends account details to the update host.
    let cancelled = false;
    let polling = false;
    const timer = window.setInterval(() => {
      setCallBusy(updateIsBlocked());
      if (!inFlight.current || polling) return;
      polling = true;
      void invoke<UpdateStatus>("desktop_update", { operation: "status" })
        .then(next => { if (!cancelled) setStatus(next); })
        .catch(() => undefined).finally(() => { polling = false; });
    }, 1_000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, []);

  const value = { status, busy, callBusy, run };
  const show = status.version && dismissed !== status.version
    && ["available", "downloading", "ready", "installing"].includes(status.phase);
  return <Context.Provider value={value}>
    {children}
    {show && <aside aria-label="Link desktop update" className="fixed bottom-4 right-4 z-[80] w-[min(380px,calc(100vw-2rem))] rounded-2xl border border-cloud-200 bg-white p-5 text-cloud-800 shadow-xl">
      <div className="flex items-start justify-between gap-3">
        <h2 className="flex items-center gap-2 font-semibold"><Download className="h-4 w-4" />{desktopAppLabel(marker()?.platform)} update · {status.version}</h2>
        <button type="button" aria-label="Remind me next time" disabled={busy} onClick={() => setDismissed(status.version)} className="ui-glass-icon rounded-full p-1 text-cloud-500 disabled:opacity-50"><X className="h-4 w-4" /></button>
      </div>
      <DesktopUpdateActions {...value} />
    </aside>}
  </Context.Provider>;
}

export function DesktopUpdateActions({ status, busy, callBusy, run }: UpdatesValue) {
  const downloading = status.phase === "downloading";
  const ready = status.phase === "ready";
  const percent = status.total ? Math.min(100, Math.floor(100 * status.downloaded / status.total)) : null;
  return <div className="mt-3 space-y-3">
    <p className="text-sm text-cloud-600" role="status">
      {status.phase === "installing" ? "Confirm the restart in Link's dialog."
        : downloading ? `Downloading and verifying the update${percent !== null ? `… ${percent}%` : "…"}`
        : ready ? "The app update is verified but not installed yet. Choose Restart to update when you're ready."
        : status.phase === "current" ? "Your installed app is up to date."
        : status.phase === "checking" ? "Checking for updates…"
        : status.phase === "available" ? `App version ${status.version} is available. You're still running ${status.currentVersion}. You can keep using Link while it downloads.`
        : "Link checks for app updates automatically. You choose when to install and restart."}
    </p>
    {downloading && <progress aria-label="Update download progress" className="h-2 w-full accent-strawberry-500" max={100} value={percent ?? undefined} />}
    {status.message && <p role="alert" className="text-sm text-rose-600">{status.message}</p>}
    {ready && callBusy && !busy && <p className="text-sm text-cloud-600">Finish your call before restarting. Link won't restart automatically.</p>}
    {ready ? <Button disabled={busy || callBusy} onClick={() => void run("install")}><RefreshCw className="mr-2 h-4 w-4" />Restart to update</Button>
      : status.phase === "available" ? <Button disabled={busy} onClick={() => void run("download")}><Download className="mr-2 h-4 w-4" />Download update</Button>
      : <Button variant="outline" disabled={busy || downloading || status.phase === "installing"} onClick={() => void run("check")}>Check for updates</Button>}
  </div>;
}

export function DesktopUpdateSettings() {
  const updates = useContext(Context);
  if (!updates) return null;
  return <section className="max-w-2xl space-y-5">
    <header><h2 className="text-2xl font-semibold text-cloud-800">App updates</h2><p className="mt-1 text-sm text-cloud-500">The chat interface and installed app update separately.</p></header>
    <ClientVersionDetails desktop={{ ...marker(), version: updates.status.currentVersion }} />
    <div className="rounded-2xl border border-cloud-200 bg-cloud-50/50 p-5">
      {updates.status.phase === "disabled" ? <p className="text-sm text-cloud-600">Automatic updates are available in the Windows release app. This development preview does not download or install updates.</p> : <DesktopUpdateActions {...updates} />}
    </div>
    <p className="text-sm text-cloud-500">Desktop updates are verified before installation. Updating doesn't sign you out or reset your account.</p>
  </section>;
}
